import type { TopicTier } from '../services/page-comparison';
import type { QuickCompareTopicFitData } from './quick-compare-topic-fit';
import { normalizeUrlForCompare } from './topic-supporting-pages';

export const TOPIC_FIT_WEAK_AUTHORITY_MAX = 15;

export interface TopicFitSupportingPagesTopic {
  topicSlug: string;
  topicLabel: string;
  yourPages: Array<{ url: string }>;
  theirPages: Array<{ url: string }>;
}

export function isWeakTopicFitTier(tier: TopicTier | null): boolean {
  return tier === 'unclassified' || tier === 'aspirational';
}

export function isWeakTopicFitAuthority(authorityScore: number | null): boolean {
  return authorityScore !== null && authorityScore < TOPIC_FIT_WEAK_AUTHORITY_MAX;
}

export function isWeakTopicFit(topicFit: Pick<QuickCompareTopicFitData, 'authorityScore' | 'tier'>): boolean {
  return isWeakTopicFitAuthority(topicFit.authorityScore) || isWeakTopicFitTier(topicFit.tier);
}

/** Other supporting URLs on your site for the focal topic (excluding the focal URL). */
export function countOtherSupportingPagesOnYourSite(
  topics: TopicFitSupportingPagesTopic[],
  mainTopicSlug: string,
  focalUrl: string
): number {
  const slugKey = (mainTopicSlug || '').toLowerCase().trim();
  if (!slugKey) return 0;
  const focalKey = normalizeUrlForCompare(focalUrl);
  const topic = topics.find((row) => row.topicSlug.toLowerCase().trim() === slugKey);
  if (!topic) return 0;
  return topic.yourPages.filter((page) => normalizeUrlForCompare(page.url) !== focalKey).length;
}

export function competitorHasSupportingPagesOnTopic(
  topics: TopicFitSupportingPagesTopic[],
  mainTopicSlug: string
): boolean {
  const slugKey = (mainTopicSlug || '').toLowerCase().trim();
  if (!slugKey) return false;
  const topic = topics.find((row) => row.topicSlug.toLowerCase().trim() === slugKey);
  return (topic?.theirPages?.length ?? 0) > 0;
}

export interface TopicFitSupportingPagesInsight {
  show: boolean;
  topicLabel: string;
}

export function buildTopicFitSupportingPagesInsight(input: {
  topicFit: QuickCompareTopicFitData;
  supportingTopics: TopicFitSupportingPagesTopic[];
  focalUrl: string;
  side: 'primary' | 'compare';
}): TopicFitSupportingPagesInsight {
  const empty: TopicFitSupportingPagesInsight = { show: false, topicLabel: '' };
  const { topicFit, supportingTopics, focalUrl, side } = input;

  if (side !== 'primary') return empty;
  if (!topicFit.topicFound || !topicFit.mainTopicSlug) return empty;
  if (!isWeakTopicFit(topicFit)) return empty;

  const otherCount = countOtherSupportingPagesOnYourSite(
    supportingTopics,
    topicFit.mainTopicSlug,
    focalUrl
  );
  if (otherCount > 0) return empty;

  return {
    show: true,
    topicLabel: topicFit.mainTopicLabel || topicFit.mainTopicSlug
  };
}
